import gsap from 'gsap'
import { createContext, useCallback, useContext, useState } from 'react'
import { useGSAP } from '@gsap/react'
import { arrayEqual, mappedArray } from '@10000/common'
import { useBreakpoints, useDelayedValue, useView } from '@10000/react'
import { Box, CenteredBox, PracticePie } from '../../widgets/index.js'
import { practiceAverages } from '../../../utilities.js'
import { WheelPin } from './WheelPin.jsx'

const context = createContext({ update: () => {}, hide: () => {} })

export const usePracticeWheel = () => useContext(context)

const rotationAngle = (practice, extraSpins = 0) => (-practice * 60) + (extraSpins * 360) - 30

export const PracticeWheel = ({ displayMode, children }) => {
  const { contextSafe } = useGSAP()
  const { question, teamAverages, highlightedResult } = useView('squiz')
  const practice = Math.floor(question / 2)

  const { mobile } = useBreakpoints()
  const fullSize = mobile ? 350 : displayMode ? 1230 : 'min(64vw, 1230px, 80vh)'
  const small = displayMode ? 720 : 'min(90vw, 70vh)'

  const [bottom, setBottom] = useState(-1230)
  const [size, setSize] = useState(1230)
  const [values, setValues] = useState(mappedArray(6, -1))
  const [headings, setHeadings] = useState(false)
  const [pb, setPb] = useState(0)
  const [pinActive, setPinActive] = useState(false)

  const showPin = useDelayedValue(pinActive, { false: 1000 })

  const selectPractice = contextSafe(practice => {
    gsap.timeline()
      .to('#practice-wheel', { rotation: 0, duration: 0 })
      .to('#practice-wheel', { rotation: rotationAngle(practice), duration: 0 })
  })

  const update = useCallback(({ bottom, size, values, headings, pb, pinActive }, delay = 0) => {
    setTimeout(() => {
      bottom !== undefined && setBottom(bottom)
      size && setSize(size)
      setValues(current => (arrayEqual(current?.flat(), values?.flat()) || values === undefined) ? current : values)
      headings !== undefined && setHeadings(headings)
      pb !== undefined && setPb(pb)
      pinActive !== undefined && setPinActive(pinActive)
    }, delay)
  }, [])

  const showResult = useCallback(value => {
    setTimeout(() => selectPractice(practice), 600)
    update({ size: fullSize, bottom: 0, headings: false, pinActive: false }, 600)
    update({ values: mappedArray(6, i => i === practice ? value : -1) }, 1600)
  }, [practice, fullSize, update, selectPractice])

  const showWheel = () => {
    update({ size: small, bottom: '50%', pinActive: false })
    update({ headings: true, values: practiceAverages(teamAverages) }, 1000)
  }

  const hide = useCallback(() => {
    update({ size: fullSize, bottom: -1230, headings: false, values: mappedArray(6, -1), pinActive: false })
  }, [update, fullSize])

  const pulse = useCallback((delay = 0.4) => {
    const slice = `#practice-wheel .slice-background:nth-child(${practice + 1}) path`
    gsap.timeline()
      .set(slice, { fill: 'var(--blue)' })
      .set(slice, { fill: '#1E1E1E', delay: 0.2 })
      .set(slice, { fill: 'var(--blue)', delay })
      .set(slice, { fill: '#1E1E1E', delay: 0.2 })
  }, [practice])

  const spin = useCallback(() => {
    const slice = `#practice-wheel .slice-background:nth-child(${practice + 1}) path`

    update({ size: small, bottom: '50%', headings: false, values: mappedArray(6, -1) })

    return gsap.timeline()
      .to('#practice-wheel .logo', { opacity: 1, duration: 0.5, delay: 1 })
      .to('#practice-wheel', { rotation: 0, duration: 0 }, '<')
      .call(() => setPinActive(true))
      .to('#practice-wheel', { rotation: rotationAngle(practice, 5), ease: 'power1.out', duration: 3 })
      .set(slice, { fill: 'var(--blue)' })
      .set(slice, { fill: '#1E1E1E', delay: 0.2 })
      .set(slice, { fill: 'var(--blue)', delay: 0.4 })
      .set(slice, { fill: '#1E1E1E', delay: 0.2 })
      .call(() => setPinActive(false))
  }, [practice, small, update])

  return (
    <context.Provider value={{ update, showResult, showWheel, spin, pulse, hide }}>
      {children}
      <Box fullHeight fullWidth pb={pb} pt={displayMode || 120} zIndex={bottom < 0 ? -1 : 0} transition={0.5}>
        <CenteredBox container fullWidth fullHeight overflow="hidden">
          <Box position="absolute" column alignItems="center" bottom={bottom} transition={1} transform="translateY(50%)">
            <Box position="absolute" top={-20} zIndex={1} opacity={showPin ? 1 : 0} transition={0.25}>
              <WheelPin
                segments={30}
                duration={2.8}
                active={pinActive}
              />
            </Box>

            <Box container column alignItems="center" id="practice-wheel">
              <PracticePie
                size={size}
                values={values}
                headings={headings}
                transition={1}
                highlightedSegment={highlightedResult}
              />
            </Box>
          </Box>
        </CenteredBox>
      </Box>
    </context.Provider>
  )
}
